import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Wrench, Building2, LogOut,
  ChevronDown, Check, KeyRound, Search,
} from 'lucide-react';

// Menu structure. `link` = active item routed to a real path. `inactive: true`
// renders dimmed and non-clickable. Items in dropdowns may have an optional
// `match` regex used to highlight the parent menu when the user is on a
// matching route.
const MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, link: '/' },
  {
    id: 'clientes', label: 'Clientes',
    items: [
      { label: 'Nuevo', inactive: true },
      { label: 'Listado', link: '/clients' },
      { label: 'Créditos', link: '/credits' },
      { label: 'Pines', inactive: true },
    ],
    match: /^\/(clients|credits)/,
  },
  {
    id: 'articulos', label: 'Artículos',
    items: [
      { label: 'Inventario', link: '/inventory' },
      { label: 'Mínimos', link: '/minimos' },
      { label: 'Traspasos', inactive: true },
      { label: 'Pedidos', link: '/purchase-orders' },
    ],
    match: /^\/(inventory|minimos|purchase-orders)/,
  },
  {
    id: 'reparaciones', label: 'Reparaciones',
    items: [
      { label: 'Listado', link: '/repairs' },
      { label: 'Estadísticas', inactive: true },
    ],
    match: /^\/repairs/,
  },
  {
    id: 'ventas', label: 'Ventas',
    items: [
      { label: 'Punto de Venta', link: '/pos' },
      { label: 'Historial', link: '/sales' },
      { label: 'Caja', link: '/cash-register' },
    ],
    match: /^\/(pos|sales|cash-register|returns)/,
  },
];

const ADMIN_MENU = {
  id: 'admin', label: 'Admin',
  items: [
    { label: 'Usuarios', link: '/users' },
    { label: 'Sucursales', link: '/branches' },
    { label: 'Checador', link: '/checador' },
    { label: 'Configuración', link: '/settings' },
  ],
  match: /^\/(users|branches|checador|settings)/,
};

function HoverDropdown({ entry, isActive }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const closeTimer = useRef(null);
  const btnRef = useRef(null);
  const show = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const hideDelayed = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4 });
  }, [open]);
  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hideDelayed} data-testid={`menu-${entry.id}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
          isActive
            ? 'bg-[#AFBD20] text-[#1D3C6E]'
            : 'text-white/65 hover:text-white hover:bg-white/5'
        }`}
      >
        {entry.label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && createPortal(
        <div
          style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 2147483000 }}
          className="min-w-[180px] bg-white rounded-md shadow-lg ring-1 ring-black/10 py-1"
          onMouseEnter={show}
          onMouseLeave={hideDelayed}
          data-testid={`menu-${entry.id}-dropdown`}
        >
          {entry.items.map((it, i) => {
            const key = `${entry.id}-${i}`;
            if (it.inactive) {
              return (
                <div key={key} className="px-3 py-1.5 text-sm text-slate-300 cursor-not-allowed select-none"
                  data-testid={`menu-${entry.id}-item-inactive`} title="Próximamente">
                  {it.label}
                </div>
              );
            }
            return (
              <NavLink
                key={key}
                to={it.link}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-1.5 text-sm transition-colors ${
                    isActive ? 'bg-slate-100 text-[#1D3C6E] font-medium' : 'text-slate-700 hover:bg-slate-50'
                  }`
                }
                data-testid={`menu-${entry.id}-item`}
              >
                {it.label}
              </NavLink>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

function ClickDropdown({ trigger, children, align = 'right', testid }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, right: 0 });
  const ref = useRef(null);
  const triggerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [open]);
  return (
    <div ref={triggerRef} className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-2">
        {trigger}
      </button>
      {open && createPortal(
        <div
          ref={ref}
          style={align === 'right'
            ? { position: 'fixed', right: pos.right, top: pos.top, zIndex: 2147483000 }
            : { position: 'fixed', left: pos.left, top: pos.top, zIndex: 2147483000 }}
          className="min-w-[200px] bg-white rounded-md shadow-lg ring-1 ring-black/10 py-1"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout, currentBranch, setCurrentBranch, branches } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchInput, setSearchInput] = useState('');
  const handleSearchSubmit = (e) => {
    if (e.key !== 'Enter') return;
    const q = searchInput.trim();
    if (q.length < 2) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const isAdmin = user?.role === 'admin';
  const allMenu = isAdmin ? [...MENU, ADMIN_MENU] : MENU;
  const currentBranchName = branches.find(b => b.id === currentBranch)?.name || 'Sin sucursal';

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex flex-col h-screen overflow-hidden" data-testid="app-layout">
      {/* Top Navigation Bar */}
      <header className="bg-[#1D3C6E] flex items-center px-3 gap-3 flex-shrink-0 shadow-sm h-14 relative z-[1000]" data-testid="topbar">
        {/* Brand */}
        <div className="flex items-center gap-2.5 pr-3 border-r border-white/10 h-full" data-testid="brand-block">
          <div className="w-9 h-9 rounded-lg bg-[#AFBD20] flex items-center justify-center">
            <Wrench className="w-5 h-5 text-[#1D3C6E]" />
          </div>
          <span className="text-lg font-bold text-white font-['Outfit'] tracking-tight">Fixif</span>
        </div>

        {/* Nav items */}
        <nav className="flex-1 flex items-center gap-1 overflow-x-auto h-full" data-testid="topbar-nav">
          {allMenu.map((entry) => {
            const isMatch = entry.match ? entry.match.test(location.pathname) : false;
            if (!entry.items) {
              return (
                <NavLink
                  key={entry.id}
                  to={entry.link}
                  end={entry.link === '/'}
                  data-testid={`menu-${entry.id}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-[#AFBD20] text-[#1D3C6E]'
                        : 'text-white/65 hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  {entry.icon && <entry.icon className="w-4 h-4" />}
                  <span>{entry.label}</span>
                </NavLink>
              );
            }
            return <HoverDropdown key={entry.id} entry={entry} isActive={isMatch} />;
          })}
        </nav>

        {/* Right side: search + branch + user */}
        <div className="flex items-center gap-2 h-full pl-3 border-l border-white/10">
          <div className="relative hidden md:block" data-testid="topbar-search">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchSubmit}
              placeholder="Buscar…"
              data-testid="topbar-search-input"
              className="h-8 pl-8 pr-3 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 text-xs focus:outline-none focus:ring-2 focus:ring-[#AFBD20]/40 focus:bg-white/15 w-52"
            />
          </div>
          {branches.length > 0 && (
            <ClickDropdown
              testid="branch-picker"
              trigger={
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 border border-white/20 text-white text-xs hover:bg-white/15">
                  <Building2 className="w-3.5 h-3.5 text-[#AFBD20]" />
                  <span className="font-medium">{currentBranchName}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </div>
              }
            >
              {branches.map(b => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setCurrentBranch(b.id)}
                  className={`flex items-center justify-between w-full px-3 py-1.5 text-sm hover:bg-slate-50 ${
                    b.id === currentBranch ? 'text-[#1D3C6E] font-medium' : 'text-slate-700'
                  }`}
                  data-testid={`branch-option-${b.id}`}
                >
                  <span>{b.name}</span>
                  {b.id === currentBranch && <Check className="w-4 h-4 text-[#AFBD20]" />}
                </button>
              ))}
            </ClickDropdown>
          )}

          <ClickDropdown
            testid="user-menu"
            trigger={
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5">
                <div className="w-7 h-7 rounded-full bg-[#AFBD20]/20 flex items-center justify-center">
                  <span className="text-[11px] font-bold text-[#AFBD20]">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                <div className="hidden sm:flex flex-col leading-tight">
                  <span className="text-xs font-medium text-white truncate max-w-[100px]">{user?.name}</span>
                  <span className="text-[10px] text-white/50 capitalize">{user?.role}</span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-white/60" />
              </div>
            }
          >
            <div className="px-3 py-1.5 text-sm text-slate-300 cursor-not-allowed select-none flex items-center gap-2"
              data-testid="user-menu-change-password" title="Próximamente">
              <KeyRound className="w-3.5 h-3.5" />
              <span>Cambiar Clave</span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="logout-button"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Salir</span>
            </button>
          </ClickDropdown>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-slate-50">
        <div className="p-6"><Outlet /></div>
      </main>
    </div>
  );
}
